import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, School, KeyRound, CheckCircle2, ArrowLeft, UserRound } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useNavigate, Link } from 'react-router-dom'
import { AuthShell } from '@/components/AuthShell'
import { countryOptions, guessCountry } from '@/data/countries'

// ─── Validation ───────────────────────────────────────────────────────────────

const passwordStrength = z
  .string()
  .min(12, 'Must be at least 12 characters')
  .max(128)
  .regex(/[A-Z]/, 'Must contain an uppercase letter')
  .regex(/[a-z]/, 'Must contain a lowercase letter')
  .regex(/[0-9]/, 'Must contain a number')
  .regex(/[^A-Za-z0-9]/, 'Must contain a special character')

const baseFields = {
  legalName: z.string().min(2, 'Full name required'),
  email:     z.string().email('Enter a valid email address'),
  password:  passwordStrength,
  confirm:   z.string(),
  agree:     z.literal(true, { errorMap: () => ({ message: 'You must accept the Terms and Privacy Policy' }) }),
}

const inviteSchema = z.object({
  ...baseFields,
  inviteCode: z.string().min(1, 'Enter your invite code'),
}).refine(d => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] })

const schoolSchema = z.object({
  ...baseFields,
  schoolName:  z.string().min(2, 'School name required'),
  country:     z.string().regex(/^[A-Z]{2}$/, 'Choose your country'),
  town:        z.string().max(80).optional(),
  attestation: z.literal(true, { errorMap: () => ({ message: 'You must confirm this to register a school' }) }),
  signature:   z.string().min(2, 'Type your full name to sign'),
}).refine(d => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] })

type InviteForm = z.infer<typeof inviteSchema>
type SchoolForm = z.infer<typeof schoolSchema>

// ─── Shared password field ────────────────────────────────────────────────────

function PasswordField({
  id, label, show, onToggle, registration, error,
}: {
  id: string; label: string; show: boolean; onToggle: () => void
  registration: object; error?: string
}) {
  return (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      <div className="relative">
        <input id={id} type={show ? 'text' : 'password'}
          className={`input pr-10 ${error ? 'border-alert-400' : ''}`}
          {...registration} />
        <button type="button" onClick={onToggle}
          className="absolute inset-y-0 right-3 flex items-center text-portal-muted hover:text-portal-text">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-alert-600">{error}</p>}
    </div>
  )
}

// ─── Terms acceptance checkbox ────────────────────────────────────────────────

function TermsCheckbox({ registration, error }: { registration: object; error?: string }) {
  return (
    <div>
      <label className="flex items-start gap-2 text-sm text-portal-muted">
        <input type="checkbox" className="mt-0.5 h-4 w-4 flex-shrink-0" {...registration} />
        <span>
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue underline">Terms of Service</a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue underline">Privacy Policy</a>.
        </span>
      </label>
      {error && <p className="mt-1 text-xs text-alert-600">{error}</p>}
    </div>
  )
}

// ─── Invite code path ─────────────────────────────────────────────────────────

function InviteCodeForm({ onBack }: { onBack: () => void }) {
  const navigate  = useNavigate()
  const [showPw, setShowPw] = useState(false)
  const [codeInput, setCodeInput] = useState('')
  const [confirmedCode, setConfirmedCode] = useState('')

  const { data: codeInfo, isFetching, error: codeError, refetch } = useQuery({
    queryKey: ['invite-code', confirmedCode],
    queryFn:  () => api.get<{ schoolName: string; type: string }>(`/auth/validate-invite-code?code=${encodeURIComponent(confirmedCode)}`),
    enabled:  !!confirmedCode,
    retry: false,
  })

  const { register, handleSubmit, formState: { errors } } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { inviteCode: '' },
  })

  const signup = useMutation({
    mutationFn: (values: InviteForm) =>
      api.post('/auth/register', {
        mode:       'invite',
        inviteCode: values.inviteCode.toUpperCase().trim(),
        legalName:  values.legalName,
        email:      values.email,
        password:   values.password,
      }),
    onSuccess: () => navigate('/login?registered=1'),
  })

  const roleLabel = codeInfo?.type === 'teacher' ? 'Teacher' : 'Parent'

  return (
    <div>
      <button onClick={onBack} className="mb-4 flex items-center gap-1.5 text-sm text-portal-muted hover:text-portal-text">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <h2 className="mb-6 text-lg font-bold text-portal-text">Join with invite code</h2>

      <form onSubmit={handleSubmit(v => signup.mutate(v))} noValidate className="space-y-4">
        {/* Invite code + validate */}
        <div>
          <label className="label">Invite code</label>
          <div className="flex gap-2">
            <input
              type="text"
              className={`input flex-1 font-mono uppercase tracking-widest ${errors.inviteCode ? 'border-alert-400' : codeInfo ? 'border-ok-400' : ''}`}
              placeholder="ABCD1234"
              {...register('inviteCode', {
                onChange: (e) => setCodeInput(e.target.value),
              })}
            />
            <button
              type="button"
              onClick={() => { setConfirmedCode(codeInput.toUpperCase().trim()); void refetch() }}
              disabled={!codeInput.trim() || isFetching}
              className="rounded-lg border border-portal-border bg-white px-3 py-2 text-sm font-medium text-portal-text hover:bg-gray-50 disabled:opacity-40"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
            </button>
          </div>
          {errors.inviteCode && <p className="mt-1 text-xs text-alert-600">{errors.inviteCode.message}</p>}
          {codeError && (
            <p className="mt-1 text-xs text-alert-600">
              {(codeError as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Invalid code'}
            </p>
          )}
          {codeInfo && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-ok-50 px-3 py-2 text-sm text-ok-700">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span><strong>{codeInfo.schoolName}</strong> · {roleLabel} account</span>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="legalName" className="label">Full name</label>
          <input id="legalName" type="text" className={`input ${errors.legalName ? 'border-alert-400' : ''}`}
            placeholder="Jane Smith" {...register('legalName')} />
          {errors.legalName && <p className="mt-1 text-xs text-alert-600">{errors.legalName.message}</p>}
        </div>

        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input id="email" type="email" className={`input ${errors.email ? 'border-alert-400' : ''}`}
            placeholder="you@email.com" {...register('email')} />
          {errors.email && <p className="mt-1 text-xs text-alert-600">{errors.email.message}</p>}
        </div>

        <PasswordField id="password" label="Password" show={showPw} onToggle={() => setShowPw(!showPw)}
          registration={register('password')} error={errors.password?.message} />
        <PasswordField id="confirm" label="Confirm password" show={showPw} onToggle={() => setShowPw(!showPw)}
          registration={register('confirm')} error={errors.confirm?.message} />

        <TermsCheckbox registration={register('agree')} error={errors.agree?.message} />

        {signup.isError && (
          <div className="rounded-lg bg-alert-50 border border-alert-200 px-4 py-3 text-sm text-alert-700">
            {(signup.error as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message ?? 'Registration failed. Please try again.'}
          </div>
        )}

        <button type="submit" disabled={signup.isPending}
          className="btn-primary w-full justify-center py-2.5 text-base">
          {signup.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : 'Create account'}
        </button>
      </form>
    </div>
  )
}

// ─── School admin path ────────────────────────────────────────────────────────

function SchoolAdminForm({ onBack, solo = false }: { onBack: () => void; solo?: boolean }) {
  const navigate = useNavigate()
  const [showPw, setShowPw] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<SchoolForm>({
    resolver: zodResolver(schoolSchema),
    defaultValues: { country: guessCountry() },
  })
  const countries = useMemo(() => countryOptions(), [])

  const signup = useMutation({
    mutationFn: (values: SchoolForm) =>
      api.post('/auth/register', {
        mode:        'school',
        legalName:   values.legalName,
        email:       values.email,
        password:    values.password,
        schoolName:  values.schoolName,
        country:     values.country,
        town:        values.town?.trim() || undefined,
        attestation: values.attestation,
        signature:   values.signature,
      }),
    onSuccess: () => navigate('/login?registered=1'),
  })

  return (
    <div>
      <button onClick={onBack} className="mb-4 flex items-center gap-1.5 text-sm text-portal-muted hover:text-portal-text">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <h2 className="mb-6 text-lg font-bold text-portal-text">
        {solo ? 'Set up your classes' : 'Register your school'}
      </h2>

      <form onSubmit={handleSubmit(v => signup.mutate(v))} noValidate className="space-y-4">
        <div>
          <label htmlFor="schoolName" className="label">
            {solo ? 'Name for your classes or tutoring' : 'School name'}
          </label>
          <input id="schoolName" type="text" className={`input ${errors.schoolName ? 'border-alert-400' : ''}`}
            placeholder={solo ? 'Jane Smith Tutoring' : 'Springfield Primary School'} {...register('schoolName')} />
          {solo && <p className="hint">Parents see this name. You can change it later in Settings.</p>}
          {errors.schoolName && <p className="mt-1 text-xs text-alert-600">{errors.schoolName.message}</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="country" className="label">Country</label>
            <select id="country" className={`input ${errors.country ? 'border-alert-400' : ''}`} {...register('country')}>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            {errors.country && <p className="mt-1 text-xs text-alert-600">{errors.country.message}</p>}
          </div>
          <div>
            <label htmlFor="town" className="label">Town or city <span className="text-portal-muted">(optional)</span></label>
            <input id="town" type="text" className="input" placeholder={solo ? 'Yangon' : 'Springfield'} {...register('town')} />
          </div>
        </div>

        <div>
          <label htmlFor="legalName" className="label">Your full name</label>
          <input id="legalName" type="text" className={`input ${errors.legalName ? 'border-alert-400' : ''}`}
            placeholder="Jane Smith" {...register('legalName')} />
          {errors.legalName && <p className="mt-1 text-xs text-alert-600">{errors.legalName.message}</p>}
        </div>

        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input id="email" type="email" className={`input ${errors.email ? 'border-alert-400' : ''}`}
            placeholder={solo ? 'you@example.com' : 'admin@school.edu'} {...register('email')} />
          {errors.email && <p className="mt-1 text-xs text-alert-600">{errors.email.message}</p>}
        </div>

        <PasswordField id="password" label="Password" show={showPw} onToggle={() => setShowPw(!showPw)}
          registration={register('password')} error={errors.password?.message} />
        <PasswordField id="confirm" label="Confirm password" show={showPw} onToggle={() => setShowPw(!showPw)}
          registration={register('confirm')} error={errors.confirm?.message} />

        {/* Child-safety attestation */}
        <div className="rounded-xl border border-sun-200 bg-sun-50 p-3 space-y-2">
          <label className="flex items-start gap-2 text-sm text-sun-900">
            <input type="checkbox" className="mt-0.5 h-4 w-4 flex-shrink-0" {...register('attestation')} />
            <span>
              {solo
                ? 'I confirm I teach or tutor these children, and that I have (or will get) consent from a parent or guardian for every child I add.'
                : 'I confirm we are a legitimate school or educational organisation, that I am authorised to set up Litlings for our pupils, and that we have obtained (or will obtain) parental/guardian consent for every child we add.'}
            </span>
          </label>
          {errors.attestation && <p className="text-xs text-alert-600">{errors.attestation.message}</p>}
          <input
            type="text"
            className={`input ${errors.signature ? 'border-alert-400' : ''}`}
            placeholder="Type your full name to sign"
            {...register('signature')}
          />
          {errors.signature && <p className="text-xs text-alert-600">{errors.signature.message}</p>}
        </div>

        <TermsCheckbox registration={register('agree')} error={errors.agree?.message} />

        {signup.isError && (
          <div className="rounded-lg bg-alert-50 border border-alert-200 px-4 py-3 text-sm text-alert-700">
            {(signup.error as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message ?? 'Registration failed. Please try again.'}
          </div>
        )}

        <button type="submit" disabled={signup.isPending}
          className="btn-primary w-full justify-center py-2.5 text-base">
          {signup.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : solo ? 'Create my account' : 'Register school'}
        </button>
      </form>
    </div>
  )
}

// ─── Mode selector ────────────────────────────────────────────────────────────

type Mode = 'select' | 'invite' | 'school' | 'solo'

export default function SignupPage() {
  const [mode, setMode] = useState<Mode>('select')

  return (
    <AuthShell
      title="Create your account"
      subtitle="Free for 60 days. No credit card."
      tagline="Set up your class in five minutes."
    >
        {mode === 'select' && (
          <div className="space-y-3">
            <button
              onClick={() => setMode('solo')}
              className="flex w-full items-center gap-4 rounded-[18px] bg-portal-bg-alt p-5 text-left transition-colors hover:bg-brand-red-soft"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-red">
                <UserRound className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-portal-text">Just me: a teacher or tutor</p>
                <p className="text-sm text-portal-muted">Your own classes or tutoring groups. Free for up to 40 children</p>
              </div>
            </button>
            <button
              onClick={() => setMode('invite')}
              className="flex w-full items-center gap-4 rounded-[18px] bg-portal-bg-alt p-5 text-left transition-colors hover:bg-brand-yellow-soft"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-yellow">
                <KeyRound className="h-5 w-5 text-brand-ink" />
              </div>
              <div>
                <p className="font-semibold text-portal-text">I have an invite code</p>
                <p className="text-sm text-portal-muted">Join a school as a teacher or parent</p>
              </div>
            </button>

            <button
              onClick={() => setMode('school')}
              className="flex w-full items-center gap-4 rounded-[18px] bg-portal-bg-alt p-5 text-left transition-colors hover:bg-brand-blue-soft"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-blue">
                <School className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-portal-text">Register a school</p>
                <p className="text-sm text-portal-muted">For a head or office manager setting up several teachers</p>
              </div>
            </button>
          </div>
        )}

        {mode === 'invite' && <InviteCodeForm onBack={() => setMode('select')} />}
        {mode === 'school' && <SchoolAdminForm onBack={() => setMode('select')} />}
        {mode === 'solo' && <SchoolAdminForm solo onBack={() => setMode('select')} />}

        <p className="mt-8 text-sm text-portal-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-brand-blue hover:underline">Sign in</Link>
        </p>
    </AuthShell>
  )
}
